import type { SCAPIShopperStoresSchemas } from 'src/types';

type Store = SCAPIShopperStoresSchemas['Store'];

/** A contiguous run of days that share the same opening hours, e.g. "Mon–Fri" → "09:00 - 18:00". */
export type HoursGroup = { key: string; label: string; hours: string };

type SpecialHoursEntry = {
    date: string;
    business_hours: string[];
};

/**
 * Builds the single-line, human-readable address for a store, e.g.
 * "Via Fenoglietti 14, Torino 10126 (TO)". Missing parts are skipped.
 * Shared between the "Location e contatti" tab and the store-info drawer.
 */
export function formatStoreAddress(store: Store): string {
    const street = [store.address1, store.address2].filter(Boolean).join(' ');
    const cityAndPostal = [store.city, store.postalCode].filter(Boolean).join(' ');
    const withState = store.stateCode ? `${cityAndPostal} (${store.stateCode})` : cityAndPostal;
    return [street, withState].filter(Boolean).join(', ');
}

/**
 * Builds a Google Maps directions URL for the store, preferring precise
 * coordinates and falling back to the formatted address. Returns `undefined`
 * when there is nothing to point at.
 */
export function buildStoreDirectionsUrl(store: Store): string | undefined {
    if (typeof store.latitude === 'number' && typeof store.longitude === 'number') {
        return `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
    }

    const address = formatStoreAddress(store);
    if (address) {
        const destination = [store.name, address].filter(Boolean).join(', ');
        return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    }

    return undefined;
}

/**
 * Builds short weekday labels for the given locale, indexed Monday (`0`) through
 * Sunday (`6`) to match the `c_businessHours{0..6}` store attributes.
 */
export function getDayLabels(locale: string): string[] {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });

    return Array.from({ length: 7 }, (_, index) => {
        // 2024-01-01 is a Monday, so index 0 maps to Monday.
        const label = formatter.format(new Date(Date.UTC(2024, 0, 1 + index)));
        return label.charAt(0).toUpperCase() + label.slice(1);
    });
}

/** Formats a single day's `c_businessHours` list into a human-readable string, e.g. "09:00 - 13:00, 15:00 - 19:00". */
export function formatDayHours(hours: string[] | undefined, closedLabel: string): string {
    if (!hours || hours.length === 0) {
        return closedLabel;
    }

    const ranges: string[] = [];
    for (let i = 0; i + 1 < hours.length; i += 2) {
        ranges.push(`${hours[i]} - ${hours[i + 1]}`);
    }

    return ranges.length > 0 ? ranges.join(', ') : closedLabel;
}

/** Parses the `c_specialHours` JSON string into a typed array, returning an empty list on invalid input. */
function parseSpecialHours(raw: string | undefined): SpecialHoursEntry[] {
    if (!raw?.trim()) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.flatMap((entry) => {
            if (
                typeof entry !== 'object' ||
                entry === null ||
                typeof (entry as SpecialHoursEntry).date !== 'string' ||
                !Array.isArray((entry as SpecialHoursEntry).business_hours)
            ) {
                return [];
            }

            return [
                {
                    date: (entry as SpecialHoursEntry).date,
                    business_hours: (entry as SpecialHoursEntry).business_hours,
                },
            ];
        });
    } catch {
        return [];
    }
}

/** Returns a local `YYYY-MM-DD` date string (defaults to today) for comparing against special-hours dates. */
function getLocalDateString(date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/** Adds `days` to a `YYYY-MM-DD` string and returns the resulting local date string. */
function addDaysToDateString(dateString: string, days: number): string {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + days);
    return getLocalDateString(date);
}

/** True when `nextDate` is exactly the calendar day after `date`. */
function areConsecutiveDates(date: string, nextDate: string): boolean {
    return addDaysToDateString(date, 1) === nextDate;
}

/** Keeps only special-hours entries falling within the next 30 days, sorted ascending. */
function filterUpcomingSpecialHours(entries: SpecialHoursEntry[]): SpecialHoursEntry[] {
    const today = getLocalDateString();
    const maxDate = addDaysToDateString(today, 30);

    return entries
        .filter((entry) => entry.date > today && entry.date <= maxDate)
        .sort((a, b) => a.date.localeCompare(b.date));
}

/** Formats a special-hours `YYYY-MM-DD` date into a short label, e.g. "25 Dec". */
function formatSpecialDateLabel(dateString: string, locale: string): string {
    const [year, month, day] = dateString.split('-').map(Number);
    const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    const label = formatter.format(new Date(Date.UTC(year, month - 1, day)));
    return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Groups upcoming special-hours overrides into contiguous runs that share the same
 * hours, producing labels like "25 Dec" or "24 Dec–26 Dec".
 */
export function groupSpecialHours(entries: SpecialHoursEntry[], locale: string, closedLabel: string): HoursGroup[] {
    const upcoming = filterUpcomingSpecialHours(entries);
    if (upcoming.length === 0) {
        return [];
    }

    const daily = upcoming.map((entry) => ({
        date: entry.date,
        hours: formatDayHours(entry.business_hours, closedLabel),
    }));

    const groups: HoursGroup[] = [];
    let start = 0;
    for (let i = 1; i <= daily.length; i += 1) {
        const isEndOfGroup =
            i === daily.length ||
            daily[i].hours !== daily[start].hours ||
            !areConsecutiveDates(daily[i - 1].date, daily[i].date);

        if (isEndOfGroup) {
            const label =
                start === i - 1
                    ? formatSpecialDateLabel(daily[start].date, locale)
                    : `${formatSpecialDateLabel(daily[start].date, locale)}–${formatSpecialDateLabel(daily[i - 1].date, locale)}`;
            groups.push({ key: `special-${daily[start].date}`, label, hours: daily[start].hours });
            start = i;
        }
    }

    return groups;
}

/**
 * Groups the seven `c_businessHours{0..6}` weekday lists into contiguous runs that share
 * the same hours, producing labels like "Mon" or "Mon–Fri".
 */
export function groupBusinessHours(store: Store, dayLabels: string[], closedLabel: string): HoursGroup[] {
    const daily = [
        store.c_businessHours0,
        store.c_businessHours1,
        store.c_businessHours2,
        store.c_businessHours3,
        store.c_businessHours4,
        store.c_businessHours5,
        store.c_businessHours6,
    ].map((hours) => formatDayHours(hours, closedLabel));

    const groups: HoursGroup[] = [];
    let start = 0;
    for (let i = 1; i <= daily.length; i += 1) {
        if (i === daily.length || daily[i] !== daily[start]) {
            const label = start === i - 1 ? dayLabels[start] : `${dayLabels[start]}–${dayLabels[i - 1]}`;
            groups.push({ key: `regular-${start}`, label, hours: daily[start] });
            start = i;
        }
    }

    return groups;
}

/**
 * Builds the full list of store opening-hours groups for display: the regular weekly
 * schedule followed by any upcoming special-hours overrides. Returns an empty list when
 * the store has no configured hours.
 */
export function getStoreHoursGroups(
    store: Store,
    locale: string,
    closedLabel: string,
    dayLabels: string[]
): HoursGroup[] {
    const hoursGroups = groupBusinessHours(store, dayLabels, closedLabel);
    const specialHoursGroups = groupSpecialHours(parseSpecialHours(store.c_specialHours), locale, closedLabel);
    return [...hoursGroups, ...specialHoursGroups];
}
